/**
 * Synesthesia - A lightweight syntax-highlighting editor core
 * @license MIT
 */

import { generateStyles } from './styles.js';
import { getTheme, mergeTheme, solar, cave, themeToCSSVars, createTheme, applyThemeToElement } from './themes.js';
import { Parser, ParserRegistry, parserRegistry, PlainTextParser, JavaScriptParser } from './parser.js';

/**
 * Synesthesia Editor Class
 */
class Synesthesia {
    // Static properties
    static instances = new WeakMap();
    static stylesInjected = false;
    static globalListenersInitialized = false;
    static instanceCount = 0;

    /**
     * Constructor - Always returns an array of instances
     * @param {string|Element|NodeList|Array} target - Target element(s)
     * @param {Object} options - Configuration options
     * @returns {Array} Array of Synesthesia instances
     */
    constructor(target, options = {}) {
      let elements;
      if (typeof target === 'string') {
        elements = Array.from(document.querySelectorAll(target));
        if (elements.length === 0) throw new Error(`No elements found for selector: ${target}`);
      } else if (target instanceof Element) {
        elements = [target];
      } else if (target instanceof NodeList) {
        elements = Array.from(target);
      } else if (Array.isArray(target)) {
        elements = target;
      } else {
        throw new Error('Invalid target: must be selector string, Element, NodeList, or Array');
      }

      const instances = elements.map(element => {
        if (element.synesthesiaInstance) {
          element.synesthesiaInstance.reinit(options);
          return element.synesthesiaInstance;
        }
        const instance = Object.create(Synesthesia.prototype);
        instance._init(element, options);
        element.synesthesiaInstance = instance;
        Synesthesia.instances.set(element, instance);
        return instance;
      });

      return instances;
    }

    /**
     * Internal initialization
     * @private
     */
    _init(element, options = {}) {
      this.element = element;
      this.instanceTheme = options.theme || null;
      this.options = this._mergeOptions(options);
      this.instanceId = ++Synesthesia.instanceCount;
      this.initialized = false;

      Synesthesia.injectStyles();
      Synesthesia.initGlobalListeners();

      const container = element.querySelector('.synesthesia-container');
      if (container) {
        this._recoverFromDOM(container);
      } else {
        this._buildFromScratch();
      }

      this.initialized = true;
      if (this.options.onChange) {
        this.options.onChange(this.getValue(), this);
      }
    }

    /**
     * Merge user options with defaults
     * @private
     */
    _mergeOptions(options) {
      const defaults = {
        fontSize: '14px',
        lineHeight: 1.6,
        fontFamily: '"SF Mono", SFMono-Regular, Menlo, Monaco, "Cascadia Code", Consolas, "Roboto Mono", "Noto Sans Mono", "Droid Sans Mono", "Ubuntu Mono", "DejaVu Sans Mono", "Liberation Mono", "Courier New", Courier, monospace',
        padding: '16px',
        parser: null, // Parser instance or function
        language: 'plaintext', // Default language
        textareaProps: {},
        autofocus: false,
        autoResize: false,
        minHeight: '100px',
        maxHeight: null,
        placeholder: 'Start typing...',
        value: '',
        onChange: null,
        onKeydown: null,
      };
      const { theme, colors, ...cleanOptions } = options;
      return { ...defaults, ...cleanOptions };
    }

    /**
     * Recover from existing DOM structure
     * @private
     */
    _recoverFromDOM(container) {
      this.container = container;
      this.wrapper = container.querySelector('.synesthesia-wrapper');
      this.textarea = this.wrapper?.querySelector('.synesthesia-input');
      this.preview = this.wrapper?.querySelector('.synesthesia-preview');

      // More thorough validation of DOM structure
      const isValidStructure = !!(
        this.wrapper && 
        this.textarea && 
        this.preview &&
        this.wrapper.parentElement === this.container &&
        this.textarea.parentElement === this.wrapper &&
        this.preview.parentElement === this.wrapper
      );

      if (!isValidStructure) {
        // Preserve current value if possible
        const currentValue = this.textarea?.value || container.textContent || '';
        this.container.remove();
        this._buildFromScratch();
        if (currentValue) {
          this.setValue(currentValue);
        }
        return;
      }

      // Verify and restore instance binding
      this.wrapper._instance = this;
      
      // Restore event listeners that might have been lost
      this._restoreEventListeners();
      
      // Apply current configuration
      this._applyInstanceStyles();
      this._configureTextarea();
      this._applyOptions();
    }

    /**
     * Restore event listeners on recovered DOM
     * @private
     */
    _restoreEventListeners() {
      // Check if listeners are already attached by testing response
      const testEvent = new Event('input', { bubbles: false });
      let hasListener = false;
      
      const testHandler = () => { hasListener = true; };
      this.textarea.addEventListener('input', testHandler, { once: true });
      this.textarea.dispatchEvent(testEvent);
      this.textarea.removeEventListener('input', testHandler);
      
      // If no listeners detected, we need to ensure global listeners will handle it
      if (!hasListener && !Synesthesia.globalListenersInitialized) {
        Synesthesia.initGlobalListeners();
      }
    }

    /**
     * Build editor from scratch
     * @private
     */
    _buildFromScratch() {
      const content = this.element.textContent || '';
      this.element.innerHTML = '';
      this._createDOM();
      this.setValue(content || this.options.value);
      this._applyOptions();
    }

    /**
     * Create DOM structure
     * @private
     */
    _createDOM() {
      this.container = document.createElement('div');
      this.container.className = 'synesthesia-container';

      const themeToUse = this.instanceTheme || Synesthesia.currentTheme || solar;
      const themeName = typeof themeToUse === 'string' ? themeToUse : themeToUse.name;
      if (themeName) {
        this.container.setAttribute('data-theme', themeName);
      }
      if (this.instanceTheme) {
        const themeObj = typeof this.instanceTheme === 'string' ? getTheme(this.instanceTheme) : this.instanceTheme;
        if (themeObj?.colors) {
          this.container.style.cssText += themeToCSSVars(themeObj.colors);
        }
      }

      this.wrapper = document.createElement('div');
      this.wrapper.className = 'synesthesia-wrapper';
      this.wrapper._instance = this;
      this._applyInstanceStyles();

      this.textarea = document.createElement('textarea');
      this.textarea.className = 'synesthesia-input';
      this.textarea.placeholder = this.options.placeholder;
      this._configureTextarea();

      // Apply any native textarea properties (same as Overtype)
      if (this.options.textareaProps) {
        Object.entries(this.options.textareaProps).forEach(([key, value]) => {
          if (key === 'className' || key === 'class') {
            this.textarea.className += ' ' + value;
          } else if (key === 'style' && typeof value === 'object') {
            Object.assign(this.textarea.style, value);
          } else {
            this.textarea.setAttribute(key, value);
          }
        });
      }

      this.preview = document.createElement('div');
      this.preview.className = 'synesthesia-preview';
      this.preview.setAttribute('aria-hidden', 'true');

      this.wrapper.append(this.textarea, this.preview);
      this.container.appendChild(this.wrapper);
      this.element.appendChild(this.container);

      if (this.options.autoResize) {
        this._setupAutoResize();
      }
    }
    
    _applyInstanceStyles() {
        if (this.options.fontSize) this.wrapper.style.setProperty('--instance-font-size', this.options.fontSize);
        if (this.options.lineHeight) this.wrapper.style.setProperty('--instance-line-height', String(this.options.lineHeight));
        if (this.options.padding) this.wrapper.style.setProperty('--instance-padding', this.options.padding);
    }

    _configureTextarea() {
      this.textarea.setAttribute('autocomplete', 'off');
      this.textarea.setAttribute('autocorrect', 'off');
      this.textarea.setAttribute('autocapitalize', 'off');
      this.textarea.setAttribute('spellcheck', 'false');
    }

    _applyOptions() {
      if (this.options.autofocus) this.textarea.focus();
      if (this.options.autoResize && !this.container.classList.contains('synesthesia-auto-resize')) {
        this._setupAutoResize();
      } else if (!this.options.autoResize) {
        this.container.classList.remove('synesthesia-auto-resize');
      }
      this.updatePreview();
    }

    updatePreview() {
      const text = this.textarea.value;
      let html;
      
      // Handle parser - can be a function, Parser instance, or null (use language)
      if (typeof this.options.parser === 'function') {
        html = this.options.parser(text);
      } else if (this.options.parser instanceof Parser) {
        html = this.options.parser.parse(text);
      } else {
        // Use language to get parser from registry
        const parser = parserRegistry.getByLanguage(this.options.language) || new PlainTextParser();
        html = parser.parse(text);
      }
      
      this.preview.innerHTML = html || '&#8203;'; // Use a zero-width space to ensure consistent height
      if (this.options.onChange && this.initialized) {
        this.options.onChange(text, this);
      }
    }

    handleInput() {
      this.updatePreview();
    }

    handleKeydown(event) {
      if (event.key === 'Tab') {
        event.preventDefault();
        
        const start = this.textarea.selectionStart;
        const end = this.textarea.selectionEnd;
        
        // Use execCommand to preserve undo history
        if (document.execCommand) {
          // For simple insertion (no selection)
          if (start === end) {
            document.execCommand('insertText', false, '  ');
          } else {
            // Handle selection - for now just replace with spaces
            // Could be enhanced to handle multi-line indent/outdent like Overtype
            document.execCommand('insertText', false, '  ');
          }
        } else {
          // Fallback to direct manipulation if execCommand is not available
          const value = this.textarea.value;
          this.textarea.value = value.substring(0, start) + '  ' + value.substring(end);
          this.textarea.selectionStart = this.textarea.selectionEnd = start + 2;
        }
        
        // Trigger input event to update preview
        this.textarea.dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }
      
      if (this.options.onKeydown) {
        this.options.onKeydown(event, this);
      }
    }

    handleScroll() {
      this.preview.scrollTop = this.textarea.scrollTop;
      this.preview.scrollLeft = this.textarea.scrollLeft;
    }

    getValue() {
      return this.textarea.value;
    }

    setValue(value) {
      this.textarea.value = value;
      this.updatePreview();
      if (this.options.autoResize) this._updateAutoHeight();
    }

    focus() { this.textarea.focus(); }
    blur() { this.textarea.blur(); }
    isInitialized() { return this.initialized; }

    reinit(options = {}) {
      this.options = this._mergeOptions({ ...this.options, ...options });
      this._applyOptions();
      this.updatePreview();
    }

    setLanguage(language) {
      this.options.language = language;
      this.options.parser = null; // Clear custom parser
      this.updatePreview();
    }

    setParser(parser) {
      this.options.parser = parser;
      this.updatePreview();
    }

    _setupAutoResize() {
      this.container.classList.add('synesthesia-auto-resize');
      this._updateAutoHeight();
      this.textarea.addEventListener('input', () => this._updateAutoHeight());
      window.addEventListener('resize', () => this._updateAutoHeight());
    }

    _updateAutoHeight() {
      if (!this.options.autoResize) return;
      
      const { textarea, preview, wrapper } = this;
      
      // Get computed styles
      const computed = window.getComputedStyle(textarea);
      const paddingTop = parseFloat(computed.paddingTop);
      const paddingBottom = parseFloat(computed.paddingBottom);
      
      // Store scroll positions
      const scrollTop = textarea.scrollTop;
      
      // Reset height to get accurate scrollHeight
      textarea.style.setProperty('height', 'auto', 'important');
      
      // Calculate new height based on scrollHeight
      let newHeight = textarea.scrollHeight;
      
      // Apply min height constraint
      if (this.options.minHeight) {
        const minHeight = parseInt(this.options.minHeight);
        newHeight = Math.max(newHeight, minHeight);
      }
      
      // Apply max height constraint
      let overflow = 'hidden';
      if (this.options.maxHeight) {
        const maxHeight = parseInt(this.options.maxHeight);
        if (newHeight > maxHeight) {
          newHeight = maxHeight;
          overflow = 'auto';
        }
      }
      
      // Apply the new height to all elements with !important to override base styles
      const heightPx = newHeight + 'px';
      textarea.style.setProperty('height', heightPx, 'important');
      textarea.style.setProperty('overflow-y', overflow, 'important');
      preview.style.setProperty('height', heightPx, 'important');
      preview.style.setProperty('overflow-y', overflow, 'important');
      wrapper.style.setProperty('height', heightPx, 'important');
      
      // Restore scroll position
      textarea.scrollTop = scrollTop;
    }

    destroy() {
      this.element.synesthesiaInstance = null;
      Synesthesia.instances.delete(this.element);
      if (this.container) {
        this.element.textContent = this.getValue();
        this.container.remove();
      }
      this.initialized = false;
    }

    // ===== Static Methods =====
    static init(target, options = {}) {
      return new Synesthesia(target, options);
    }

    static getInstance(element) {
      return element.synesthesiaInstance || Synesthesia.instances.get(element) || null;
    }

    static destroyAll() {
      document.querySelectorAll('.synesthesia-container').forEach(container => {
        const instance = Synesthesia.getInstance(container.parentElement);
        if (instance) instance.destroy();
      });
    }

    static injectStyles(force = false) {
      if (Synesthesia.stylesInjected && !force) return;
      document.querySelector('style.synesthesia-styles')?.remove();
      const theme = Synesthesia.currentTheme || solar;
      const styles = generateStyles({ theme });
      const styleEl = document.createElement('style');
      styleEl.className = 'synesthesia-styles';
      styleEl.textContent = styles;
      document.head.appendChild(styleEl);
      Synesthesia.stylesInjected = true;
    }

    static setTheme(theme, customColors = null) {
      let themeObj = typeof theme === 'string' ? getTheme(theme) : theme;
      if (customColors) themeObj = mergeTheme(themeObj, customColors);
      Synesthesia.currentTheme = themeObj;
      Synesthesia.injectStyles(true);
      document.querySelectorAll('.synesthesia-container').forEach(container => {
        const themeName = themeObj.name;
        if (themeName) container.setAttribute('data-theme', themeName);
        const instance = container.parentElement?.synesthesiaInstance;
        if (instance) instance.updatePreview();
      });
    }

    static initGlobalListeners() {
      if (Synesthesia.globalListenersInitialized) return;
      const findInstance = (target) => {
        // Check if target has closest method (is an Element)
        if (!target || typeof target.closest !== 'function') return null;
        return target.closest('.synesthesia-wrapper')?._instance;
      };
      
      document.addEventListener('input', (e) => findInstance(e.target)?.handleInput(e), true);
      document.addEventListener('keydown', (e) => findInstance(e.target)?.handleKeydown(e), true);
      document.addEventListener('scroll', (e) => findInstance(e.target)?.handleScroll(e), true);
      
      document.addEventListener('selectionchange', () => {
        const instance = findInstance(document.activeElement);
        if (instance) {
          clearTimeout(instance._selectionTimeout);
          instance._selectionTimeout = setTimeout(() => instance.updatePreview(), 50);
        }
      });
      Synesthesia.globalListenersInitialized = true;
    }
}

// Export theme utilities
Synesthesia.themes = { solar, cave };
Synesthesia.getTheme = getTheme;
Synesthesia.mergeTheme = mergeTheme;
Synesthesia.createTheme = createTheme;
Synesthesia.applyThemeToElement = applyThemeToElement;
Synesthesia.themeToCSSVars = themeToCSSVars;
Synesthesia.currentTheme = solar;

// Export parser utilities
Synesthesia.Parser = Parser;
Synesthesia.ParserRegistry = ParserRegistry;
Synesthesia.parserRegistry = parserRegistry;
Synesthesia.PlainTextParser = PlainTextParser;
Synesthesia.JavaScriptParser = JavaScriptParser;

if (typeof window !== 'undefined') {
  window.Synesthesia = Synesthesia;
}

export default Synesthesia;
export { Synesthesia, Parser, ParserRegistry, parserRegistry };
